import { useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

interface ElegantHeroProps {
  title: string;
  subtitle?: string;
  tag?: string;
  breadcrumb?: { label: string; to?: string }[];
  backgroundImage?: string;
  minHeight?: string;
  align?: 'center' | 'left';
}

/* ─── Particle ─── */
interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  opacity: number;
  duration: number;
  delay: number;
  driftX: number;
  driftY: number;
}

/* ─── Data Stream Line ─── */
interface StreamLine {
  id: number;
  x: number;
  height: number;
  duration: number;
  delay: number;
  opacity: number;
}

export default function ElegantHero({
  title,
  subtitle,
  tag,
  breadcrumb,
  backgroundImage,
  minHeight = '520px',
  align = 'left',
}: ElegantHeroProps) {
  const [scrollY, setScrollY] = useState(0);
  const [mounted, setMounted] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  /* Generate stable random particles */
  const particles = useMemo<Particle[]>(() =>
    Array.from({ length: 28 }, (_, i) => ({
      id: i,
      x: (i * 37.3 + 11) % 100,
      y: (i * 53.7 + 23) % 100,
      size: 1.5 + (i % 4) * 0.8,
      opacity: 0.15 + (i % 5) * 0.1,
      duration: 6 + (i % 7) * 2,
      delay: (i % 8) * 0.7,
      driftX: ((i % 5) - 2) * 12,
      driftY: -20 - (i % 6) * 8,
    })), []
  );

  /* Generate stable data stream lines */
  const streamLines = useMemo<StreamLine[]>(() =>
    Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: 4 + i * 8.5,
      height: 30 + (i % 5) * 20,
      duration: 3 + (i % 4) * 1.5,
      delay: i * 0.4,
      opacity: 0.06 + (i % 3) * 0.04,
    })), []
  );

  useEffect(() => {
    setMounted(true);
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const heroH = heroRef.current?.offsetHeight ?? 520;
  const progress = Math.min(scrollY / heroH, 1);
  const contentOpacity = Math.max(1 - progress * 1.6, 0);
  const parallaxY = scrollY * 0.25;

  return (
    <div
      ref={heroRef}
      className="relative w-full overflow-hidden flex items-end"
      style={{ minHeight, height: '55vh' }}
    >
      {/* ── Background ── */}
      {backgroundImage ? (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${backgroundImage})`,
              transform: `translateY(${parallaxY * 0.3}px) scale(1.05)`,
              willChange: 'transform',
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(180deg, rgba(10,25,47,0.75) 0%, rgba(10,25,47,0.85) 60%, rgba(10,25,47,0.95) 100%)',
            }}
          />
        </>
      ) : (
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #0A192F 0%, #102A43 50%, #0A192F 100%)',
            transform: `translateY(${parallaxY * 0.15}px)`,
            willChange: 'transform',
          }}
        />
      )}

      {/* ── Grid Overlay (AI/Tech feel) ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(201,162,39,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(201,162,39,0.04) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      {/* ── Scan Line ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(201,162,39,0.25) 30%, rgba(201,162,39,0.5) 50%, rgba(201,162,39,0.25) 70%, transparent 100%)',
            animation: 'heroScanLine 6s ease-in-out infinite',
            willChange: 'transform, opacity',
          }}
        />
      </div>

      {/* ── Data Stream Lines ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {streamLines.map((line) => (
          <div
            key={line.id}
            style={{
              position: 'absolute',
              left: `${line.x}%`,
              top: 0,
              width: '1px',
              height: `${line.height}%`,
              background: `linear-gradient(180deg, transparent 0%, rgba(201,162,39,${line.opacity * 3}) 40%, rgba(201,162,39,${line.opacity}) 100%)`,
              animation: `heroDataStream ${line.duration}s ease-in-out ${line.delay}s infinite`,
              willChange: 'transform, opacity',
            }}
          />
        ))}
      </div>

      {/* ── Floating Particles ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((p) => (
          <div
            key={p.id}
            style={{
              position: 'absolute',
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              borderRadius: '50%',
              background: '#C9A227',
              opacity: p.opacity,
              animation: `heroFloat ${p.duration}s ease-in-out ${p.delay}s infinite`,
              '--drift-x': `${p.driftX}px`,
              '--drift-y': `${p.driftY}px`,
              willChange: 'transform, opacity',
            } as React.CSSProperties}
          />
        ))}
      </div>

      {/* ── Ambient Glow ── */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute"
          style={{
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(201,162,39,0.1) 0%, transparent 65%)',
            top: '-20%',
            right: '10%',
            filter: 'blur(60px)',
            animation: 'heroPulseGlow 8s ease-in-out infinite',
          }}
        />
        <div
          className="absolute"
          style={{
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(201,162,39,0.06) 0%, transparent 65%)',
            bottom: '10%',
            left: '15%',
            filter: 'blur(50px)',
            animation: 'heroPulseGlow 10s ease-in-out 2s infinite',
          }}
        />
      </div>

      {/* ── Corner Accent (top-right) ── */}
      <div
        className="absolute top-0 right-0 pointer-events-none"
        style={{
          width: '120px',
          height: '120px',
          borderTop: '1px solid rgba(201,162,39,0.2)',
          borderRight: '1px solid rgba(201,162,39,0.2)',
          animation: mounted ? 'heroCornerExpand 1.2s ease forwards' : 'none',
        }}
      />
      <div
        className="absolute bottom-0 left-0 pointer-events-none"
        style={{
          width: '80px',
          height: '80px',
          borderBottom: '1px solid rgba(201,162,39,0.15)',
          borderLeft: '1px solid rgba(201,162,39,0.15)',
          animation: mounted ? 'heroCornerExpand 1.4s ease 0.2s forwards' : 'none',
          opacity: 0,
        }}
      />

      {/* ── Gold accent line top ── */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(201,162,39,0.4), transparent)' }}
      />

      {/* ── Content ── */}
      <div
        className="relative z-10 w-full"
        style={{
          paddingBottom: '64px',
          paddingTop: '120px',
          transform: `translateY(${parallaxY}px)`,
          opacity: contentOpacity,
          willChange: 'transform, opacity',
        }}
      >
        <div className={`max-w-[1400px] mx-auto px-6 md:px-10 ${align === 'center' ? 'text-center' : ''}`}>

          {/* Breadcrumb */}
          {breadcrumb && breadcrumb.length > 0 && (
            <nav
              className="mb-5 text-sm flex items-center gap-2 flex-wrap"
              style={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(10px)',
                transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s',
              }}
            >
              {breadcrumb.map((crumb, i) => (
                <span key={i} className="flex items-center gap-2">
                  {i > 0 && (
                    <span style={{ color: 'rgba(255,255,255,0.2)' }}>/</span>
                  )}
                  {crumb.to ? (
                    <Link
                      to={crumb.to}
                      className="no-underline transition-colors duration-300"
                      style={{ color: 'rgba(255,255,255,0.4)' }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.7)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.4)';
                      }}
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span style={{ color: 'rgba(255,255,255,0.65)' }}>{crumb.label}</span>
                  )}
                </span>
              ))}
            </nav>
          )}

          {/* Tag */}
          {tag && (
            <div
              className={`flex items-center gap-3 mb-5 ${align === 'center' ? 'justify-center' : ''}`}
              style={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateX(0)' : 'translateX(-16px)',
                transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s',
              }}
            >
              <div
                className="w-8 h-px"
                style={{
                  background: 'linear-gradient(90deg, #C9A227, transparent)',
                  transform: mounted ? 'scaleX(1)' : 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 0.8s ease 0.3s',
                }}
              />
              <p
                className="text-xs tracking-widest-plus uppercase"
                style={{
                  color: '#C9A227',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                }}
              >
                {tag}
              </p>
            </div>
          )}

          {/* Title */}
          <h1
            className="text-4xl md:text-5xl lg:text-6xl leading-tight mb-5"
            style={{
              fontFamily: "'Noto Serif JP', 'Playfair Display', serif",
              fontWeight: 500,
              color: '#FFFFFF',
              letterSpacing: '0.04em',
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.8s ease 0.35s, transform 0.8s ease 0.35s',
            }}
          >
            {title}
          </h1>

          {/* Subtitle */}
          {subtitle && (
            <p
              className="text-lg md:text-xl leading-relaxed max-w-xl"
              style={{
                color: '#FFFFFF',
                fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
                fontWeight: 400,
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(16px)',
                transition: 'opacity 0.8s ease 0.5s, transform 0.8s ease 0.5s',
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* ── Bottom gold line ── */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(201,162,39,0.3), transparent)' }}
      />

      {/* ── Keyframes ── */}
      <style>{`
        @keyframes heroScanLine {
          0%   { top: -2px; opacity: 0; }
          5%   { opacity: 1; }
          90%  { opacity: 0.6; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes heroDataStream {
          0%   { transform: translateY(-100%); opacity: 0; }
          10%  { opacity: 1; }
          80%  { opacity: 0.6; }
          100% { transform: translateY(400%); opacity: 0; }
        }
        @keyframes heroFloat {
          0%   { transform: translate(0, 0); opacity: var(--base-opacity, 0.2); }
          33%  { transform: translate(calc(var(--drift-x) * 0.5), calc(var(--drift-y) * 0.5)); }
          66%  { transform: translate(var(--drift-x), var(--drift-y)); opacity: calc(var(--base-opacity, 0.2) * 1.5); }
          100% { transform: translate(0, 0); opacity: var(--base-opacity, 0.2); }
        }
        @keyframes heroPulseGlow {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50%       { opacity: 1;   transform: scale(1.15); }
        }
        @keyframes heroCornerExpand {
          from { opacity: 0; transform: scale(0.6); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
